<template>
  <div>
    <main
      id="main"
      ref="main"
      class="u-container pb-0 contact-info-desktop"
    >
      <h1 class="font-bold">
        USPS Change-of-Address<sup>®</sup>
      </h1>
      <IndividualProgressBarWithSteps
        :mobile="false"
      />
      <form
        ref="form"
        novalidate
        class="pb-0"
        @submit.prevent="validate(); analytics.form.submitted();"
      >
        <section class="flex py-6">
          <div class="w-1/2 max:pr-xl py-6">
            <Tooltip
              placement="top"
              @show="analytics.click.tooltipForwardType"
            >
              <h3
                ref="forward-header"
                class="font-bold"
              >
                Is This a Permanent or Temporary Move?
              </h3>
              <template slot="tip">
                <p>
                  If you plan to move to a new address for less than 6 months and then to
                  a different address (not your old address), choose "Permanent" and fill
                  out another Change-of-Address request at that time.
                </p>
                <p v-if="isMultipleBusinessesEnabled">
                  <br>
                  The forwarding type is applied to all businesses listed under the saved
                  businesses section. You will have an opportunity to change the forwarding
                  type when you reach the verify & pay step.
                </p>
              </template>
              <template slot="accessible">
                Permanent versus temporary description
              </template>
            </Tooltip>
            <fieldset class="simplify__forward-type-container">
              <SimplifyInputRadio
                ref="permanent"
                v-model="forwardType"
                title="Permanent"
                description="You do not plan to move back to your
                            old address (most movers are permanent)."
                class="simplify-forward-type-button"
                form-value="PERMANENT"
                name="forward-type"
                @input="forwardType = $event; analytics.click.forwardType('PERMANENT')"
              />
              <SimplifyInputRadio
                ref="temporary"
                v-model="forwardType"
                title="Temporary"
                description="You plan to move back to your old address in less than six months."
                class="simplify-forward-type-button"
                form-value="TEMPORARY"
                name="forward-type"
                @input="forwardType = $event; analytics.click.forwardType('TEMPORARY')"
              />
              <p
                v-if="errors.forwardType.hasError"
                class="errors text-red-200 text-sm font-bold mt-1"
              >
                {{ errors.forwardType.message }}
              </p>
            </fieldset>
          </div>
        </section>
        <div class="c-hairline" />
        <section class="flex py-6">
          <div class="w-1/2 max:pr-xl py-6">
            <Tooltip
              placement="top"
              @show="analytics.click.tooltipForwardStartDate"
            >
              <h3
                ref="date-header"
                class="font-bold mb-5"
              >
                Mail Forwarding Start Date
              </h3>
              <template slot="tip">
                <p>
                  Enter the date you'd like us to begin forwarding your mail to
                  your new address. The date you enter must not be more than 30
                  days prior to today, or more than 3 months from today. Mail
                  should arrive at your new address 7-10 Postal business days
                  after your move effective date OR your file date, whichever
                  date is later.
                </p>
                <p
                  v-if="multipleAddresses === 'YES'"
                  class="mt-4"
                >
                  The forwarding start <span v-if="forwardType !== 'PERMANENT'">and end</span>
                  date applies to businesses listed under saved businesses. You will have an
                  opportunity to change the forwarding start
                  <span v-if="forwardType !== 'PERMANENT'">and end</span>
                  date when you reach the verify & pay step.
                </p>
              </template>
              <template slot="accessible">
                Forwarding dates description
              </template>
            </Tooltip>
            <InputDatePicker
              ref="startDate"
              v-model="startDate"
              :label="forwardType !== 'TEMPORARY' ? 'Mail Forwarding Date' : 'Start Forwarding'"
              :min="minStartDate"
              :max="maxStartDate"
              desktop
              name="forward-start-date"
              class="mt-3 simplify__mail-forwarding-date"
              :has-error="errors.startDate.hasError"
              @input="analytics.input.startDate()"
            >
              <span slot="helper">
                {{ errors.startDate.message }}
              </span>
            </InputDatePicker>
            <InputDatePicker
              v-if="forwardType === 'TEMPORARY'"
              ref="stopDate"
              v-model="stopDate"
              :min="minStopDate"
              :max="maxStopDate"
              :open-date="minStopDate"
              desktop
              label="End Forwarding"
              name="forward-stop-date"
              class="mt-3 simplify__mail-forwarding-date"
              :has-error="errors.stopDate.hasError"
              @input="analytics.input.stopDate()"
            >
              <span slot="helper">
                {{ errors.stopDate.message }}
              </span>
            </InputDatePicker>
          </div>
        </section>
        <div class="c-hairline" />
        <section class="flex py-6">
          <!-- Individual/Family Contact Information -->

          <div class="w-1/2 py-6">
            <Tooltip
              placement="top"
              @show="analytics.click.tooltipInformation"
            >
              <h3
                ref="info-header"
                class="font-bold mb-4"
              >
                Mover Contact Information
              </h3>
              <template
                v-if="moverType === 'BUSINESS'"
                slot="tip"
              >
                <div v-if="!isMultipleBusinessesEnabled">
                  <p>
                    Enter the primary business name in this field.
                    <template v-if="flagBusinessAliasEnabled">
                      If you receive mail by more than one business name,
                      please include each business alias name in the expandable
                      fields.
                    </template>
                  </p>
                  <p
                    v-if="flagBusinessAliasEnabled"
                    class="mt-3"
                  >
                    Each alias name added will be treated as its own Change-of-Address
                    order. Only one email address will be collected and applied to all
                    primary and alias names.
                  </p>
                </div>
                <div v-else>
                  <p>
                    The email and phone are applied to all businesses listed under the saved
                    businesses section. Only the email and phone of the primary business address
                    under the saved businesses section can be edited.
                  </p>
                </div>
              </template>
              <template
                v-else
                slot="tip"
              >
                <p>
                  <span class="font-bold">
                    First Name (or Initial):
                  </span>
                  Enter your first name or initial.
                </p>
                <p class="mt-3">
                  <span class="font-bold">
                    Middle Name (or Initial):
                  </span>
                  Enter your middle name or initial.
                </p>
                <p class="mt-3">
                  <span class="font-bold">
                    Last Name:
                  </span>
                  Fill in only one last name. If anyone with the same
                  last name is moving to a different address, use a separate
                  Change-of-Address for each person.
                </p>
              </template>
              <template slot="accessible">
                Contact information description
              </template>
            </Tooltip>
            <!-- Business Specific Inputs -->
            <template v-if="moverType === 'BUSINESS'">
              <InputText
                v-if="!isMultipleBusinessesEnabled"
                ref="businessName"
                v-model.trim="businessName"
                label="Business Name"
                placeholder="Business Name"
                name="business-name"
                class="mt-5"
                :has-error="errors.businessName.hasError"
                @blur="analytics.input.businessName"
                @input="errors.businessName.hasError = false"
              >
                <span
                  v-if="errors.businessName.hasError"
                  slot="helper"
                >
                  {{ errors.businessName.message }}
                </span>
              </InputText>
              <template
                v-if="flagBusinessAliasEnabled && showBusinessAlias
                  && !isMultipleBusinessesEnabled"
              >
                <div class="c-hairline mt-5 mb-5" />
                <BusinessAlias
                  v-if="enhancedBusinessAliasesEnabled"
                  ref="businessAliases"
                  :value="businessAliases"
                  :field-error="fieldErrors"
                  :analytics="analytics"
                  :business-name="businessName"
                  :initial-alias-count="enhancedBusinessAliasesInitialCount"
                  :aliases-increment="enhancedBusinessAliasesIncrementCount"
                  :max-alias-count="enhancedBusinessAliasesMaxCount"
                />

                <template v-if="!enhancedBusinessAliasesEnabled">
                  <InputText
                    ref="businessAlias1"
                    v-model.trim="businessAlias1"
                    label="Business Alias 1"
                    placeholder="Business Alias 1"
                    name="business-alias-1"
                    class="mt-5"
                    :has-error="errors.businessAlias1.hasError"
                    @blur="analytics.input.businessAlias('1')"
                  >
                    <span
                      v-if="errors.businessAlias1.hasError"
                      slot="helper"
                    >
                      {{ errors.businessAlias1.message }}
                    </span>
                  </InputText>
                  <InputText
                    ref="businessAlias2"
                    v-model.trim="businessAlias2"
                    label="Business Alias 2"
                    placeholder="Business Alias 2"
                    name="business-alias-2"
                    class="mt-5"
                    :has-error="errors.businessAlias2.hasError"
                    @blur="analytics.input.businessAlias('2')"
                  >
                    <span
                      v-if="errors.businessAlias2.hasError"
                      slot="helper"
                    >
                      {{ errors.businessAlias2.message }}
                    </span>
                  </InputText>
                  <InputText
                    ref="businessAlias3"
                    v-model.trim="businessAlias3"
                    label="Business Alias 3"
                    placeholder="Business Alias 3"
                    name="business-alias-3"
                    class="mt-5"
                    :has-error="errors.businessAlias3.hasError"
                    @blur="analytics.input.businessAlias('3')"
                  >
                    <span
                      v-if="errors.businessAlias3.hasError"
                      slot="helper"
                    >
                      {{ errors.businessAlias3.message }}
                    </span>
                  </InputText>
                  <InputText
                    ref="businessAlias4"
                    v-model.trim="businessAlias4"
                    label="Business Alias 4"
                    placeholder="Business Alias 4"
                    name="business-alias-4"
                    class="mt-5"
                    :has-error="errors.businessAlias4.hasError"
                    @blur="analytics.input.businessAlias('4')"
                  >
                    <span
                      v-if="errors.businessAlias4.hasError"
                      slot="helper"
                    >
                      {{ errors.businessAlias4.message }}
                    </span>
                  </InputText>
                </template>
              </template>
              <div
                v-if="!isMultipleBusinessesEnabled"
                class="business-alias-buttons"
              >
                <button
                  v-if="flagBusinessAliasEnabled"
                  id="biz-alias-button"
                  ref="toggleBusinessAliases"
                  type="button"
                  class="text-blue-100 font-bold underline mt-5"
                  @click="showBusinessAlias = !showBusinessAlias;
                          analytics.click.businessAliases(showBusinessAlias);"
                >
                  <template v-if="showBusinessAlias">
                    My business does not go by another name
                  </template>
                  <template v-else>
                    My business also goes by another name
                  </template>
                </button>
                <button
                  v-if="showBusinessAlias && enhancedBusinessAliasesEnabled"
                  id="biz-more-alias-button"
                  ref="addMoreAliases"
                  type="button"
                  class="biz-more-alias-button text-blue-100 font-bold underline mt-5"
                  @click.prevent="moreAlias()"
                >
                  <SvgPlusIcon
                    color="#3573b1"
                    :height="12"
                    :width="12"
                  /><span>Add more aliases</span>
                </button>
              </div>
            </template>
            <!-- Default non-business inputs -->
            <template v-else>
              <InputText
                ref="firstName"
                v-model.trim="firstName"
                label="First Name"
                placeholder="First Name"
                name="first-name"
                class="mt-5"
                :has-error="errors.firstName.hasError"
                :disabled="ivsProofingCompleted"
                @input="errors.firstName.hasError = false"
                @blur="analytics.input.firstName"
              >
                <span
                  v-if="errors.firstName.hasError"
                  slot="helper"
                >
                  {{ errors.firstName.message }}
                </span>
              </InputText>
              <InputText
                v-model="middleName"
                label="Middle Name or Initial"
                placeholder="Middle Name or Initial"
                name="middle-initial"
                class="mt-5"
                :has-error="middleName ? errors.middleName.hasError : false"
                :disabled="ivsProofingCompleted"
                @input="errors.middleName.hasError = false"
              >
                <span
                  v-if="middleName && errors.middleName.hasError"
                  slot="helper"
                >
                  {{ errors.middleName.message }}
                </span>
              </InputText>
              <InputText
                ref="lastName"
                v-model.trim="lastName"
                label="Last Name"
                placeholder="Last Name"
                name="last-name"
                class="mt-5"
                :has-error="errors.lastName.hasError"
                :disabled="ivsProofingCompleted"
                @blur="analytics.input.lastName"
                @input="errors.lastName.hasError = false"
              >
                <span
                  v-if="errors.lastName.hasError"
                  slot="helper"
                >
                  {{ errors.lastName.message }}
                </span>
              </InputText>
              <InputSuffix
                v-model="suffix"
                label="Suffix"
                name="suffix"
                class="mt-5"
                :disabled="ivsProofingCompleted"
              />
            </template>
            <InputText
              ref="email"
              v-model.trim="email"
              label="Email Address"
              placeholder="hello@mail.com"
              name="email"
              class="mt-5"
              :has-error="errors.email.hasError"
              :disabled="ivsProofingCompleted"
              @blur="analytics.input.email"
              @input="errors.email.hasError = false"
            >
              <span
                v-if="errors.email.hasError"
                slot="helper"
              >
                {{ errors.email.message }}
              </span>
            </InputText>
            <p
              ref="email-info"
              class="mt-5"
            >
              Please make sure your email is active and accurate.
              Your Change-of-Address confirmation and a one-time
              reminder email will be sent to this address. To opt
              out of the reminder email, <button
                ref="emailReminderOptOut"
                :class="['c-link--choice', 'c-link--inline']"
                type="button"
                @click="emailReminderOptOut"
              >
                click here
              </button>.
            </p>
            <div class="flex mt-5">
              <div class="w-1/2 mr-3">
                <InputPhone
                  ref="phone"
                  v-model="phoneNumber"
                  :label="phoneNumLabel"
                  placeholder="(000) 000-0000"
                  name="phone"
                  :has-error="errors.phoneNumber.hasError"
                  :disabled="ivsProofingCompleted"
                  @input="errors.phoneNumber.hasError = false"
                  @blur="analytics.input.phone"
                >
                  <span
                    v-if="errors.phoneNumber.hasError"
                    slot="helper"
                  >
                    {{ errors.phoneNumber.message }}
                  </span>
                </InputPhone>
              </div>
              <div class="w-1/2 ml-3">
                <InputDropdown
                  v-if="!twoFactorAuthEnabled"
                  ref="phoneType"
                  v-model="phoneType"
                  label="Phone Type"
                  name="phone-type"
                  :has-error="errors.phoneType.hasError"
                  @input="errors.firstName.hasError = false; analytics.input.phoneType()"
                >
                  <option
                    value="MOBILE"
                  >
                    Mobile
                  </option>
                  <option
                    value="HOME"
                  >
                    Home
                  </option>
                  <span
                    v-if="errors.phoneType.hasError"
                    slot="helper"
                  >
                    {{ errors.phoneType.message }}
                  </span>
                </InputDropdown>
              </div>
            </div>
            <div
              v-if="twoFactorAuthEnabled"
              class="mt-5"
            >
              <DpoPhoneNumberDisclaimer />
            </div>
            <section class="mt-10">
              <transition
                name="fade"
                appear
              >
                <section
                  v-if="renderV3Challenge && !showOutageBanner"
                  class="mb-6"
                >
                  <p class="mb-4">
                    For your security, clicking the box below confirms that this
                    Change-of-Address form is being completed by a person, and not
                    an automated query.
                  </p>
                  <vue-recaptcha
                    id="recaptcha-challenge"
                    ref="recaptchaChallenge"
                    :sitekey="getRecaptchaV2Key()"
                    @click.native="analytics.click.recaptchaVerified"
                    @verify="recaptchaChallengeToken=$event;"
                    @expired="recaptchaChallengeToken='';"
                  />
                  <transition
                    appear
                    name="fade"
                  >
                    <p
                      v-if="errors.recaptchaChallengeToken
                        && errors.recaptchaChallengeToken.hasError"
                      class="c-input-text__helper c-input-helper errors"
                    >
                      {{ errors.recaptchaChallengeToken.message }}
                    </p>
                  </transition>
                </section>
              </transition>
              <router-link
                :to="{ name: 'address-info', query: $route.query }"
                replace
                class="c-btn--secondary"
                @click.native="analytics.click.back"
              >
                Back
              </router-link>
              <button
                ref="nextBtn"
                class="c-btn ml-8"
                @click="analytics.click.next()"
              >
                Next
              </button>
            </section>
          </div>
        </section>
      </form>
    </main>
    <Modal
      ref="emailReminderOptOutModal"
      v-model="emailReminderOptOutModal"
    >
      <h4>One-Time Email Opt-Out</h4>
      <p class="mt-3">
        You've opted-out of the one-time reminder email if you don't
        complete this Change-of-Address transaction.
      </p>
      <p class="mt-3">
        You will still receive confirmation emails if you complete your
        transaction.
      </p>
      <button
        class="c-btn mt-3"
        @click="emailReminderOptOutModal=false"
      >
        Close
      </button>
    </Modal>
    <Modal
      ref="coaCheckModal"
      v-model="coaCheckModal"
    >
      <h4 class="font-bold">
        Based on your response...
      </h4>
      <p class="mt-3">
        A Change-of-Address already exists matching the name and address
        you've entered. To make changes to your current Change-of-Address,
        visit
        <a
          :class="['c-link--choice', 'c-link--inline']"
          :href="this.$sanitize($options.coacUrl)"
          rel="noopener noreferrer"
        >
          {{ $options.coacUrl }}
        </a>, enter your new ZIP Code™ and 16-digit confirmation code,
        which can be located within your confirmation email and the
        Confirmation Notification Letter you received at your new address.
      </p>
    </Modal>
  </div>
</template>
<script>
/* eslint-disable max-len */

// MGO UI Components
import VueRecaptcha from 'vue-recaptcha';
import {
  InputDatePicker,
  InputDropdown,
  InputPhone,
  InputSuffix,
  InputText,
  Modal,
  IndividualProgressBarWithSteps,
  Tooltip,
} from '@mgo/ui/lib/components';
import { track } from '../../analytics/events';

// mixin used in both Disclaimer/desktop.vue as well as MoveCombined/mobile.vue
import { desktopAnalytics } from './analytics';
import ContactInfoMixin from './mixin';
import addressViolations from '../../mixins/addressViolations';
import datePicker from '../../mixins/datePicker';
import destinationManager from '../../mixins/destinationManager';
import emailReminder from '../../mixins/emailReminder';
import SimplifyInputRadio from '../../components/SimplifyInputRadio.vue';
import BusinessAlias from '../../components/BusinessAlias.vue';
import DpoPhoneNumberDisclaimer from '../../components/DpoPhoneNumberDisclaimer.vue';
import grecaptcha from '../../mixins/grecaptcha';
import SvgPlusIcon from '../../components/SvgPlusIcon.vue';


export default {
  name: 'ContactInfoSimplify',
  components: {
    InputDatePicker,
    InputDropdown,
    InputPhone,
    InputSuffix,
    InputText,
    Modal,
    IndividualProgressBarWithSteps,
    SimplifyInputRadio,
    Tooltip,
    VueRecaptcha,
    BusinessAlias,
    DpoPhoneNumberDisclaimer,
    SvgPlusIcon,
  },
  mixins: [
    addressViolations,
    datePicker,
    destinationManager,
    emailReminder,
    grecaptcha,
    ContactInfoMixin,
  ],
  data() {
    return {
      current: 1,
      config: {},
    };
  },
  computed: {
    totalSteps() {
      return this.informedDeliveryEligible ? 7 : 6;
    },
    progressStep() {
      return this.current / this.totalSteps;
    },
    isFamily() {
      return true;
    },
    analytics() {
      return desktopAnalytics();
    },
    flagAmpersendEnabled() {
      return this.$store.get('flagAmpersendEnabled');
    },
  },
  async mounted() {
    this.$store.commit('ENABLE_LOADER');
    const config = await this.$coa.configGet();
    this.config = config;

    await this.hydrateCOA();
    await this.hydrateMover();

    this.$store.commit('DISABLE_LOADER');
    this.pageView();
    this.dirty = false;
  },
  methods: {
    showBusinessAliasHandle() {
      this.showBusinessAlias = !this.showBusinessAlias;
    },
    async submit() {
      this.improvedFieldErrors = {};
      sessionStorage.setItem('offersChoice', this.offersChoice);
      sessionStorage.setItem('autofillChoice', this.autofillChoice);
      sessionStorage.setItem('informedDeliveryEligible', this.informedDeliveryEligible);
      sessionStorage.setItem('startDate', this.startDate);
      sessionStorage.setItem('temp', this.forwardType === 'TEMPORARY');

      this.$store.commit('ENABLE_LOADER');

      const addressResponses = await this.$coa.addressSaveV2({
        addressType: 'OLD',
        streetAddress: this.oldStreetAddress,
        city: this.oldCity,
        state: this.oldState,
        zipCode: this.oldZipCode,
        urbanizationName: this.oldState === 'PR'
          ? this.oldUrbanizationName
          : null,
        privateMailbox: null,
        poBoxNumber: this.oldPoBoxNumber || null,
        recentConstruction: false,
        overrideAms: false,
        moverType: this.moverType,
        forwardType: this.forwardType,
      }, {
        addressType: 'NEW',
        streetAddress: this.newStreetAddress,
        city: this.newCity,
        state: this.newState,
        zipCode: this.newZipCode,
        urbanizationName: this.newState === 'PR'
          ? this.newUrbanizationName
          : null,
        privateMailbox: this.newPrivateMailbox || null,
        poBoxNumber: this.newPoBoxNumber || null,
        recentConstruction: this.recentConstruction,
        overrideAms: this.overrideAms,
        moverType: this.moverType,
        forwardType: this.forwardType,
      });

      if (addressResponses?.xhr?.exception) {
        if (addressResponses?.xhr?.exception === 'MilitaryZip5ForwardTypeException') {
          this.fieldErrors = [
            ...this.fieldErrors,
            { field: 'forwardType', message: addressResponses?.xhr?.message || '' },
          ];
          this.errors.forwardType.message = addressResponses?.xhr?.message || '';
          this.errors.forwardType.hasError = true;
        }
      }

      if (!this.informedDeliveryEligible) {
        this.informedDelivery = false;
      }

      if (this.twoFactorAuthEnabled) {
        this.phoneType = 'MOBILE';
      }

      const payload = {
        moverType: this.moverType,
        forwardType: this.forwardType,
        startDate: this.startDate,
        informedDelivery: this.informedDelivery,
        stopDate: this.forwardType === 'TEMPORARY' ? this.stopDate : null,
        businessName: this.moverType === 'BUSINESS' ? this.businessName : null,
        email: this.email || null,
        famPrompt1ShareLastName: this.familySameLastName
          ? this.familySameLastName === 'YES'
          : null,
        famPrompt2SameLastNameRemaining: this.familyRemaining
          ? this.familyRemaining === 'YES'
          : null,
        bizPromptEntireMoving: this.businessWholeCompany
          ? this.businessWholeCompany === 'YES'
          : null,
        multipleAddresses: this.multipleAddresses === 'YES' ?? false,
      };
      const { medUpdated, sdUpdated, xhr } = this.isMultipleBusinessesEnabled
        ? await this.$coa.businessCoaUpdate({ ...payload, multipleAddresses: true, orderUuid: this.$coa.orderUuid })
        : await this.$coa.coaUpdate(payload);

      this.attachFieldErrors({ xhr });

      // start/stop date updated due to email domain check
      this.medUpdated = medUpdated;
      this.sdUpdated = sdUpdated;

      if (xhr?.exception || this.fieldErrors?.length) {
        this.$store.commit('DISABLE_LOADER');
        await this.$nextTick();
        this.scrollToFirstError();
        return;
      }

      track('CoaUpdated',
        {
          dcoa: this.dcoa,
          forwardType: this.forwardType,
          informedDelivery: this.informedDelivery,
          startDate: this.startDate,
          stopDate: this.stopDate,
        });

      let businessAliasHandle = null;
      if (!this.isMultipleBusinessesEnabled) {
        let businessAliasPayload = [];
        if (this.showBusinessAlias) {
          businessAliasPayload = this.enhancedBusinessAliasesEnabled
            ? this.businessAliases.map((d) => d.value).filter((v) => v)
            : [
              this.businessAlias1,
              this.businessAlias2,
              this.businessAlias3,
              this.businessAlias4,
            ].filter((v) => v);
        }

        businessAliasHandle = this.flagBusinessAliasEnabled
        && this.moverType === 'BUSINESS' && this.$coa.businessAliasSave(businessAliasPayload);
      }

      const personSavePayload = {
        personType: 'MOVER',
        firstName: this.moverType !== 'BUSINESS' ? this.firstName : null,
        middleName: this.moverType !== 'BUSINESS' ? this.middleName : null,
        lastName: this.moverType !== 'BUSINESS' ? this.lastName : null,
        suffix: this.moverType !== 'BUSINESS' ? this.suffix : null,
        email: this.email,
        emailConfirm: this.email,
        [`${this.phoneType.toLowerCase()}Phone`]: this.phoneNumber,
      };

      const personHandle = this.isMultipleBusinessesEnabled
        ? this.$coa.businessPersonSave(personSavePayload) : this.$coa.personSave(personSavePayload);

      const handlers = [personHandle];
      if (!this.isMultipleBusinessesEnabled) {
        handlers.push(businessAliasHandle);
      }

      const [
        personResponse,
        businessAliasResponse,
      ] = await Promise.all(handlers);

      this.attachFieldErrors(personResponse);
      if (!this.isMultipleBusinessesEnabled) {
        this.attachFieldErrors(businessAliasResponse);
      }
      this.prepareFieldErrors(personResponse);


      track(this.moverUUID ? 'PersonUpdated' : 'PersonCreated',
        {
          personType: 'MOVER',
          phoneType: this.phoneType,
        });

      // coaCheckHandle after the old/newAddress response
      const coaCheckHandle = await this.$coa.coaCheck();

      // Throw coaCheckModal if 'CoaCheckException'
      if (coaCheckHandle?.error) {
        const { exception } = coaCheckHandle.xhr;
        if (exception === 'CoaCheckException') {
          this.coaCheckModal = true;
          this.defaultException('DUPCOAEXCEPTION');
          this.$store.commit('DISABLE_LOADER');
          return;
        }
      }

      if (this.flagAmpersendEnabled) {
        this.$amp.identify({
          mgoMoverType: this.moverType,
          mgoForwardType: this.forwardType,
          offersOptIn: JSON.parse(sessionStorage.getItem('mmco')) ?? this.offersChoice,
          voterregOptIn: JSON.parse(sessionStorage.getItem('mmcvr')) ?? this.autofillChoice,
        });
      }
      this.next({
        ...(this.medUpdated && { medUpdated: this.medUpdated }),
        ...(this.sdUpdated && { sdUpdated: this.sdUpdated }),
      }, addressResponses?.xhr?.exception);
    },
  },
};
</script>
